import type { ProjectContextDocument } from '@protoss/shared';
import { Tooltip } from '@protoss/ui';
import * as React from 'react';
import { ConfirmDeleteDialog } from '../../../components/ConfirmDeleteDialog';
import { formatDate } from '../../../lib/datetime';

interface ContextDocListProps {
  documents: ProjectContextDocument[];
  loading: boolean;
  onSelect: (doc: ProjectContextDocument) => void;
  onDelete: (doc: ProjectContextDocument) => void;
}

export function ContextDocList({ documents, loading, onSelect, onDelete }: ContextDocListProps) {
  const [deleteTarget, setDeleteTarget] = React.useState<ProjectContextDocument | null>(null);

  if (loading) {
    return <div className="ctx-docs-list__empty">Loading…</div>;
  }

  if (documents.length === 0) {
    return (
      <div className="ctx-docs-list__empty">
        <p className="ctx-docs-list__empty-title">No context documents yet</p>
        <p className="ctx-docs-list__empty-body">
          Add documents to give the AI context about this project.
        </p>
      </div>
    );
  }

  return (
    <div className="ctx-docs-list">
      {documents.map((doc) => (
        <div key={doc.uuid} className="ctx-docs-list__item">
          <button type="button" className="ctx-docs-list__item-body" onClick={() => onSelect(doc)}>
            <span className="ctx-docs-list__item-title">{doc.title}</span>
            {doc.contentText && (
              <span className="ctx-docs-list__item-preview">{doc.contentText.slice(0, 80)}</span>
            )}
            <span className="ctx-docs-list__item-meta">
              {doc.updatedAt
                ? formatDate(
                    doc.updatedAt instanceof Date ? doc.updatedAt.toISOString() : doc.updatedAt
                  )
                : ''}
            </span>
          </button>

          <Tooltip.Simple label="Delete document">
            <button
              type="button"
              className="ctx-docs-list__delete-btn"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteTarget(doc);
              }}
              aria-label="Delete document"
            >
              ×
            </button>
          </Tooltip.Simple>
        </div>
      ))}

      <ConfirmDeleteDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title="Delete document?"
        confirmKeyword={deleteTarget?.title ?? ''}
        description="This action cannot be undone. The document will be permanently removed."
        onConfirm={() => {
          if (deleteTarget) onDelete(deleteTarget);
          setDeleteTarget(null);
        }}
      />
    </div>
  );
}
